class DropdownMenu extends HTMLElement {
  bindEvents() {
    this.addEventListener('mouseenter', this.showDropdown.bind(this));
    this.addEventListener('mouseleave', this.hideDropdown.bind(this));
    document.documentElement.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  bindUI() {
    this.$details = this.querySelector('details');
    this.$summary = this.querySelector('summary');
  }

  connectedCallback() {
    this.setVars();
    this.bindUI();
    this.bindEvents();
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (this.isOpen || this.$details.getAttribute('open') !== null) {
      if (event.key === 'Escape') {
        this.hideDropdown();
      }
    }
  }

  hideDropdown() {
    this.isOpen = false;
    this.update();
  }

  setVars() {
    this.isOpen = false;
  }

  showDropdown() {
    this.isOpen = true;
    this.update();
  }

  update() {
    if (this.isOpen) {
      this.$details.setAttribute('open', 'open');
    } else {
      this.$details.removeAttribute('open');
    }
  }
}

if (!customElements.get('dropdown-menu')) {
  customElements.define('dropdown-menu', DropdownMenu);
}


class SearchResults extends HTMLElement {
  bindUI() {
    this.$loading = this.querySelector('[data-ref="$loading"]');
    this.$noResults = this.querySelector('[data-ref="$noResults"]');
    this.$resultsPlaceholder = this.querySelector('[data-ref="$resultsPlaceholder"]');
    this.$search = this.querySelector('[data-ref="$search"]');
    this.$searchResults = this.querySelector('[data-ref="$searchResults"]');
    this.$templateResultWithImage = document.querySelector('[data-ref="$templateResultWithImage"]');
    this.$templateResultWithoutImage = document.querySelector('[data-ref="$templateResultWithoutImage"]');
  }

  connectedCallback() {
    this.setVars();
    this.bindUI();
    this.update();

    this.performSearch();
  }

  /**
   * @param {Object} pagefindResult
   * @return {SearchResult}
   */
  async formatSearchResult(pagefindResult) {
    /** @type {PagefindData} */
    const data = await pagefindResult.data();

    return {
      body: data.meta.body,
      category: data.meta.category,
      publishedOn: data.meta.date,
      thumbnailImage: data.meta.image,
      title: data.meta.title,
      url: data.url,
    };
  }

  async initPagefind() {
    try {
      this.pagefind = await import('/_pagefind/pagefind.js');
      this.pagefind.init();
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.log(error);
    }
  }

  async performSearch() {
    if (!this.searchQuery) {
      return;
    }

    if (!this.pagefind) {
      await this.initPagefind();
    }

    /** @type {{ results: Object[] }} */
    let options;
    if (this.searchCategory) {
      options = {
        filters: {category: this.searchCategory},
      };
    }
    const search = await this.pagefind.search(this.searchQuery, options);
    const promises = search.results.map((result) => this.formatSearchResult(result));

    this.searchResults = await Promise.all(promises);
    this.update();
  }

  renderNoResults() {
    this.$noResults.removeAttribute('hidden');
  }

  /**
   * @param {SearchResult} searchResult
   */
  renderSearchResult(searchResult) {
    /** @type {HTMLTemplateElement} */
    let $template;
    if (searchResult.thumbnailImage) {
      $template = this.$templateResultWithImage;
    } else {
      $template = this.$templateResultWithoutImage;
    }

    const $clone = $template?.content.cloneNode(true);

    const $anchor = $clone.querySelector('a');
    if ($anchor) {
      $anchor.setAttribute('href', searchResult.url);
    }

    const $body = $clone.querySelector('.card__body');
    if ($body) {
      $body.innerHTML = searchResult.body;
    }

    const $category = $clone.querySelector('.card__eyebrow');
    if ($category) {
      if (searchResult.category) {
        $category.textContent = searchResult.category;
      } else {
        $category.parentElement.remove($category);
      }
    }

    const $mediaFrame = $clone.querySelector('.card__media .frame');
    if ($mediaFrame) {
      const $image = document.createElement('img');
      $image.alt = '';
      $image.src = searchResult.thumbnailImage.replace(/w=\d+/, 'w=800');
      $image.height = 180;
      $image.width = 320;
      $mediaFrame.replaceChildren($image);
    }

    const $title = $clone.querySelector('.card__title-text');
    if ($title) {
      const titleWords = searchResult.title.split(' ');
      $title.innerHTML = titleWords
        .map((word, index) => {
          if (index === titleWords.length - 1) {
            return `<span class="chevron">${word}</>`;
          } else {
            return word;
          }
        })
        .join(' ');
    }

    this.$searchResults.appendChild($clone);
  }

  /**
   * @param {string} input
   * @return {string}
   */
  static sanitizeInput(input) {
    if (!input) {
      return;
    }

    return String(input).replace(
      /[&<>'"]/g,
      (tag) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '\'': '&#39;',
        '"': '&quot;',
      }[tag]));
  }

  setVars() {
    this.pagefind = null;

    /** @type {SearchResult[]|null} */
    this.searchResults = null;

    const url = new URL(window.location.href);
    this.searchCategory = SearchResults.sanitizeInput(url.searchParams.get('category'));
    this.searchQuery = SearchResults.sanitizeInput(url.searchParams.get('q'));
  }

  update() {
    if (this.searchQuery && this.$search) {
      this.$search.value = this.searchQuery;
    }

    if (this.searchResults) {
      setTimeout(() => {
        this.$loading.dataset.isVisible = false;
        if (this.searchResults.length > 0) {
          this.searchResults.forEach((searchResult) => this.renderSearchResult(searchResult));
        } else {
          this.renderNoResults();
        }
      }, 500);
    }
  }
}

if (!customElements.get('search-results')) {
  customElements.define('search-results', SearchResults);
}


class SelectCategory extends HTMLElement {
  bindEvents() {
    this.$categories.addEventListener('input', this.handleInput.bind(this));
  }

  bindUI() {
    this.$categories = this.querySelector('[data-ref="$categories"]');
  }

  connectedCallback() {
    this.bindUI();
    this.bindEvents();
  }

  handleInput() {
    if (this.$categories?.value) {
      window.location.href = this.$categories.value;
    }
  }
}

if (!customElements.get('select-category')) {
  customElements.define('select-category', SelectCategory);
}


/**
 * @typedef {Object} PagefindData
 * @property {Object} meta
 * @property {string} [meta.body]
 * @property {string} [meta.category]
 * @property {string} meta.date
 * @property {string} [meta.image]
 * @property {string} meta.title
 * @property {string} url
 */


/**
 * @typedef {Object} SearchResult
 * @property {string} [body]
 * @property {string} category
 * @property {string} publishedOn
 * @property {string} [thumbnailImage]
 * @property {string} thumbnailText
 * @property {string} title
 * @property {string} url
 */
